import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ReactJson from 'react-json-view'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import { loadCatalog, buildPathTokenFrequency, renderHighlightedPath } from '../lib/data'
import { unifiedFetch, getDirectUrl, cleanupBlobUrl, DetailedFetchError } from '../lib/unifiedFetch'

// Environment variables
const DISABLE_LOCAL = import.meta.env.VITE_DISABLE_LOCAL === 'true'

function useQuery() {
  const { search } = useLocation()
  return useMemo(() => new URLSearchParams(search), [search])
}

export default function ViewPage() {
  const q = useQuery()
  const search = useLocation().search
  // Get the raw path parameter to preserve encoding for the Vite middleware
  const rawPath = new URLSearchParams(search).get('path') || ''
  // Decode for display and other uses
  const path = decodeURIComponent(rawPath)
  const nav = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [text, setText] = useState<string | null>(null)
  const [jsonObj, setJsonObj] = useState<any | null>(null)
  const [jsonlObjs, setJsonlObjs] = useState<any[] | null>(null)
  const [csvRows, setCsvRows] = useState<string[][] | null>(null)
  const [related, setRelated] = useState<string[] | null>(null)
  const [relatedFreq, setRelatedFreq] = useState<Map<string, number> | null>(null)
  const [binaryUrl, setBinaryUrl] = useState<string | null>(null)
  const [binaryLoading, setBinaryLoading] = useState(false)
  const [binaryError, setBinaryError] = useState<string | null>(null)

  const ext = path.split('.').pop()?.toLowerCase() || ''
  const isImage = ['png', 'jpg', 'jpeg', 'gif'].includes(ext)
  const isPdf = ext === 'pdf'
  const isHtml = ext === 'html' || ext === 'htm'
  const isMarkdown = ext === 'md'
  const isCompressed = ['gz', 'zip', 'tar', 'bz2', '7z'].includes(ext)
  const isCode = ['py', 'js', 'ts', 'jsx', 'tsx', 'css', 'sh', 'bash'].includes(ext)
  const isBiasEval = /(^|\/)bias-eval-results-/.test(path)
  // Only treat files as text if they have a known text extension (excluding HTML which needs special handling)
  const isText = ['json', 'jsonl', 'md', 'txt', 'csv', 'tsv', 'py', 'js', 'ts', 'jsx', 'tsx', 'css', 'sh', 'bash'].includes(ext)
  
  // This function is no longer needed - replaced by unifiedFetch

  function parseCsv(content: string): string[][] {
    const rows: string[][] = []
    let i = 0, field = '', row: string[] = [], inQuotes = false
    const pushField = () => { row.push(field); field = '' }
    const pushRow = () => { rows.push(row); row = [] }
    while (i < content.length) {
      const ch = content[i]
      if (inQuotes) {
        if (ch === '"') {
          if (content[i + 1] === '"') { field += '"'; i += 2; continue } // escaped quote
          inQuotes = false; i++; continue
        } else { field += ch; i++; continue }
      } else {
        if (ch === '"') { inQuotes = true; i++; continue }
        if (ch === ',') { pushField(); i++; continue }
        if (ch === '\n') { pushField(); pushRow(); i++; continue }
        if (ch === '\r') { // handle CRLF
          if (content[i + 1] === '\n') { pushField(); pushRow(); i += 2; continue }
          pushField(); pushRow(); i++; continue
        }
        field += ch; i++
      }
    }
    // flush last field/row
    pushField(); if (row.length) pushRow()
    return rows
  }

  // These individual fetch functions are no longer needed - replaced by unified system

  // Main effect for loading file content
  useEffect(() => {
    setError(null)
    setText(null)
    setJsonObj(null)
    setJsonlObjs(null)
    setCsvRows(null)
    setBinaryUrl(null)
    setBinaryError(null)
    
    const loadFile = async () => {
      try {
        setBinaryLoading(true)
        console.log('🔄 Loading file:', path)
        
        const result = await unifiedFetch(path)
        console.log('✅ File loaded:', { source: result.source, contentType: typeof result.content })
        
        // Handle binary files
        if (result.content instanceof Blob) {
          // For PDFs, images, and HTML that need iframe/embed
          if (result.source === 'github-raw') {
            // Use the raw URL directly
            setBinaryUrl(result.url)
          } else {
            // Use blob URL from API
            setBinaryUrl(result.url)
          }
        } 
        // Handle text content
        else if (typeof result.content === 'string') {
          const content = result.content
          
          if (ext === 'json') {
            try {
              const parsed = JSON.parse(content)
              setJsonObj(parsed)
            } catch {
              setText(content)
            }
          } else if (ext === 'jsonl') {
            const lines = content.split('\n').filter(l => l.trim())
            const objs: any[] = []
            for (const line of lines) {
              try { 
                objs.push(JSON.parse(line)) 
              } catch { 
                objs.push({ _parse_error: true, line }) 
              }
            }
            setJsonlObjs(objs)
          } else if (ext === 'csv' || ext === 'tsv') {
            const adjusted = ext === 'tsv' ? content.replace(/\t/g, ',') : content
            try { 
              setCsvRows(parseCsv(adjusted)) 
            } catch { 
              setText(content) 
            }
          } else if (isMarkdown) {
            const html = marked.parse(content) as string
            const clean = DOMPurify.sanitize(html)
            setText(clean)
          } else if (isHtml) {
            // For HTML files viewed as text, create a blob URL for iframe
            const htmlBlob = new Blob([content], { type: 'text/html' })
            const blobUrl = URL.createObjectURL(htmlBlob)
            setBinaryUrl(blobUrl)
          } else {
            setText(content)
          }
        }
        
        setBinaryError(null)
      } catch (error) {
        console.error('❌ Failed to load file:', error)
        
        // Create detailed error message
        let errorMessage = ''
        if (error instanceof DetailedFetchError) {
          errorMessage = `Failed to load file after ${error.attempts.length} attempts:\n\n`
          error.attempts.forEach((attempt, i) => {
            errorMessage += `Attempt ${i + 1}: ${attempt.method}\n`
            errorMessage += `  URL: ${attempt.url}\n`
            if (attempt.status) {
              errorMessage += `  Status: ${attempt.status}\n`
            }
            errorMessage += `  Error: ${attempt.error}\n\n`
          })
        } else {
          errorMessage = `Failed to load file: ${error instanceof Error ? error.message : String(error)}`
        }
        
        setBinaryError(errorMessage)
        setError(errorMessage)
      } finally {
        setBinaryLoading(false)
      }
    }
    
    loadFile()
  }, [path, ext, isMarkdown, isHtml])

  // Load catalog to get pre-populated related reasoning-* files when this is a runs bias-eval file
  useEffect(() => {
    setRelated(null)
    if (!isBiasEval) return
    loadCatalog()
      .then(data => {
        const inRuns = data.runs.some(r => r.path === path)
        if (!inRuns) return
        const entry = data.runs.find(r => r.path === path)!
        const rel = (entry as any).related as string[] | undefined
        if (rel && rel.length) {
          setRelated(rel)
          setRelatedFreq(buildPathTokenFrequency(rel, 'artifacts'))
        }
      })
      .catch(() => {})
  }, [path, isBiasEval])

  // These individual effects are no longer needed - handled by main effect

  // Cleanup blob URLs when component unmounts or path changes
  useEffect(() => {
    return () => {
      if (binaryUrl) {
        cleanupBlobUrl(binaryUrl)
      }
    }
  }, [binaryUrl])

  return (
    <div className="app">
      <div className="header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div className="title">View</div>
          <span className="code-inline">{path}</span>
        </div>
      </div>
      <div className="subtext"><a href={getDirectUrl(path)} download className="download-link">Download</a></div>
      {error && (
        <div style={{ color: 'crimson', marginBottom: 16, padding: 16, border: '1px solid #ccc', borderRadius: 8 }}>
          <h3>❌ Error Loading File</h3>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px', background: '#f5f5f5', padding: 12, borderRadius: 4 }}>
            {error}
          </pre>
          
          <details style={{ marginTop: 16 }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>🔍 Debug Information</summary>
            <div style={{ marginTop: 8, fontSize: '12px' }}>
              <p><strong>Search Parameter:</strong> <code>{search}</code></p>
              <p><strong>Raw Path:</strong> <code>{rawPath}</code></p>
              <p><strong>Decoded Path:</strong> <code>{path}</code></p>
              <p><strong>File Extension:</strong> <code>{ext}</code></p>
              <p><strong>File Type:</strong> {isText ? 'Text' : isImage ? 'Image' : isPdf ? 'PDF' : isHtml ? 'HTML' : isCompressed ? 'Compressed' : isCode ? 'Code' : 'Unknown'}</p>
              <p><strong>Local Access:</strong> {!DISABLE_LOCAL ? '✅ Enabled' : '❌ Disabled'}</p>
            </div>
          </details>
        </div>
      )}
      {isImage && (
        <div>
          {binaryLoading && (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-dim)' }}>
              🔄 Loading image...
            </div>
          )}
          
          {binaryError && (
            <div style={{ padding: 20, textAlign: 'center', color: 'crimson', border: '1px solid #ccc', borderRadius: 8 }}>
              ❌ {binaryError}
            </div>
          )}
          
          {binaryUrl && !binaryLoading && (
            <img src={binaryUrl} alt={path} style={{ maxWidth: '100%' }} />
          )}
        </div>
      )}
      {isPdf && (
        <div>
          {binaryLoading && (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-dim)' }}>
              🔄 Loading PDF...
            </div>
          )}
          
          {binaryError && (
            <div style={{ padding: 20, textAlign: 'center', color: 'crimson', border: '1px solid #ccc', borderRadius: 8 }}>
              ❌ {binaryError}
            </div>
          )}
          
          {binaryUrl && !binaryLoading && (
            <embed src={binaryUrl} type="application/pdf" width="100%" height="800px" />
          )}
        </div>
      )}
      {isHtml && !text && (
        <div>
          {binaryLoading && (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-dim)' }}>
              🔄 Loading HTML...
            </div>
          )}
          
          {binaryError && (
            <div style={{ padding: 20, textAlign: 'center', color: 'crimson', border: '1px solid #ccc', borderRadius: 8 }}>
              ❌ {binaryError}
            </div>
          )}
          
          {binaryUrl && !binaryLoading && (
            <iframe 
              src={binaryUrl} 
              style={{ width: '100%', height: 800, border: `1px solid var(--border)` }} 
              title={path}
              sandbox="allow-same-origin allow-scripts"
            />
          )}
        </div>
      )}
      {isMarkdown && text && (
        <div dangerouslySetInnerHTML={{ __html: text }} />
      )}
      {isText && !jsonObj && !jsonlObjs && !csvRows && !isMarkdown && (
        <pre className="code">
          {text ?? 'Loading…'}
        </pre>
      )}
      {jsonObj && (
        <div className="code" style={{ background: '#fff' }}>
          <ReactJson
            src={jsonObj}
            name={false}
            collapsed={2}
            enableClipboard={true}
            displayDataTypes={false}
            theme={{
              base00: '#ffffff', base01: '#f3f4f6', base02: '#e5e7eb', base03: '#9ca3af', base04: '#4b5563', base05: '#111827', base06: '#374151', base07: '#030712',
              base08: '#b45309', base09: '#d97706', base0A: '#f59e0b', base0B: '#16a34a', base0C: '#0891b2', base0D: '#2563eb', base0E: '#9333ea', base0F: '#dc2626'
            }}
            style={{ background: 'transparent' }}
          />
        </div>
      )}
      {jsonlObjs && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {jsonlObjs.map((obj, i) => (
            <div key={i} className="code">
              <div style={{ color: 'var(--text-dim)', fontFamily: 'monospace' }}>Line {i + 1}</div>
              <ReactJson
                src={obj}
                name={false}
                collapsed={2}
                enableClipboard={true}
                displayDataTypes={false}
                theme={{
                  base00: '#ffffff', base01: '#f3f4f6', base02: '#e5e7eb', base03: '#9ca3af', base04: '#4b5563', base05: '#111827', base06: '#374151', base07: '#030712',
                  base08: '#b45309', base09: '#d97706', base0A: '#f59e0b', base0B: '#16a34a', base0C: '#0891b2', base0D: '#2563eb', base0E: '#9333ea', base0F: '#dc2626'
                }}
                style={{ background: 'transparent' }}
              />
            </div>
          ))}
        </div>
      )}
      {csvRows && (
        <div className="table-wrap">
          <table>
            <tbody>
              {csvRows.slice(0, 2000).map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td key={c}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {csvRows.length > 2000 && <div style={{ padding: 8, color: 'var(--text-dim)' }}>Showing first 2000 rows…</div>}
        </div>
      )}
      {!isImage && !isPdf && !isHtml && !isText && (
        <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-dim)', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-elev)' }}>
          <p><strong>Preview not supported for this file type</strong></p>
          <p>File extension: <code>{ext}</code></p>
          <p>Please use the download link above to access this file.</p>
        </div>
      )}
      {isBiasEval && related && related.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h3>Related reasoning files in this run directory</h3>
          <ul style={{ marginTop: 8, listStyle: 'none', padding: 0 }}>
            {related.map(p => (
              <li key={p} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <span className="code-inline" style={{ flex: '1 1 200px', minWidth: 0, wordBreak: 'break-all' }}>{relatedFreq ? renderHighlightedPath(p, relatedFreq, related?.length || 0) : p}</span>
                  <div className="file-actions">
                    <button className="btn btn-ghost" onClick={() => nav({ pathname: '/view', search: `?path=${encodeURIComponent(p)}` })}>View</button>
                    <a href={getDirectUrl(p)} download className="download-link">Download</a>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {isBiasEval && related && related.length === 0 && (
        <div style={{ marginTop: 16, color: 'var(--text-dim)' }}>No reasoning-* files found in this run directory.</div>
      )}
    </div>
  )}


